import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { UtilService } from '@material-library';
import { Subscription } from 'rxjs';
import { BrowserSupportService } from '../../../core/browser-support.service';
import { LinkFileListService } from '../../../link-consume/services/link-file-list.service';
import { IDirItem, ITransferItem, ITransferItemUpload, ITransferView, IUploadFolderView } from '../../../../sync';
import { TransferQueueType, TRANSFER_STATUS } from '../../../transfer/models/transfer.model';
import { TransferService } from '../../../transfer/services/queue/transfer.service';
import { compare } from '../../func';
import { BlendEvent, TransferItem } from '../../models';
import { FeatureService } from '../../services/feature.service';
import { TransferItemService } from '../../../transfer/services/queue/transfer-item.service';
import { FileListService } from '../../../file-list/services/file-list.service';
import { RunUploadFolderService } from '../../../transfer/services/upload/run-upload-folder.service';
import { E2eeZipService } from '../../../transfer/services/download/zip/e2ee-zip.service';
import { E2eeZipItem } from '../../models';
import { BlendService } from '../../services/blend.service';
import { BuildTransferItemService } from '../../../transfer/services/queue/build-transfer-item.service';

@Component({
  selector: 'app-transfer',
  templateUrl: './transfer.component.html',
  styleUrls: ['./transfer.component.scss'],
})
export class TransferComponent implements OnInit, OnDestroy {
  @Input() public showFilter?: boolean = true;
  /**
   * Displays Go to file and Go to folder options on transfer item when upload completed.
   * Also, checkmark item will always visible if this flag is false
   */
  @Input() public showUploadSuccessOptions?: boolean = true;

  @Output() public readonly openFolder = new EventEmitter<{ transferItem: TransferItem; id: number }>();
  @Output() public readonly previewFile = new EventEmitter<TransferItem>();

  public transferView: ITransferView;
  public foldertransferView: IUploadFolderView;
  public isPublic: number;
  private subscription: Subscription;
  private uploadable: boolean;
  public ufile: any;
  public transferIndex = -1;
  public foldertransferIndex = -1;
  public downloadtransferIndex = -1;

  public filterBy: 'upload' | 'download' | 'all' = 'all';
  public isDisplayRetryButtonEventLogged = false;

  constructor(
    private buildTransferItemService: BuildTransferItemService,
    private linkFileListService: LinkFileListService,
    private browserSupportService: BrowserSupportService,
    private router: Router,
    private featureService: FeatureService,
    private transferService: TransferService,
    private transferItemService: TransferItemService,
    public utilService: UtilService,
    public fileListService: FileListService,
    private runUploadFolderService: RunUploadFolderService,
    private e2eeZipService: E2eeZipService,
    private blendService: BlendService,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.transferView = this.buildTransferItemService.view;
    this.foldertransferView = this.runUploadFolderService.view;
    this.router.url.includes('/dl/') ? (this.isPublic = 1) : (this.isPublic = 0);

    this.subscription = this.linkFileListService.getSubscription().subscribe((data) => {
      // do not check path list length as empty folder still needs to be uploadable
      if (data && data.pathlist && data.loaded && data.sorted) {
        this.uploadable = data.upload > 0;
      }
    });
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  public get queueLength(): number {
    return [
      ...this.transferView.upload.queue,
      ...this.transferView.download.queue,
      ...this.foldertransferView.rootItems,
      ...this.e2eeZipService.getQueueItems(),
    ].filter(
      (item) =>
        item.status !== TRANSFER_STATUS.STATUS_UPLOAD_CANCELLED &&
        item.status !== TRANSFER_STATUS.STATUS_DOWNLOAD_CANCELLED,
    ).length;
  }

  public get hasError(): boolean {
    return (
      [...this.transferView.upload.queue, ...this.transferView.download.queue].some(
        (item) => item.status > TRANSFER_STATUS.STATUS_DOWNLOAD_CANCELLED || item.status === 1000,
      ) ||
      this.foldertransferView.fileErrorCount > 1 ||
      !!this.foldertransferView.error?.code ||
      this.e2eeZipService.getQueueItems().some((item) => item.errcode)
    );
  }

  public get filetransfersText(): string {
    const completed = [
      ...this.transferView.upload.queue,
      ...this.transferView.download.queue,
      ...this.e2eeZipService.getQueueItems(),
    ].filter(
      (item) =>
        item.status === TRANSFER_STATUS.STATUS_SUCCESS || item.status > TRANSFER_STATUS.STATUS_DOWNLOAD_CANCELLED,
    ).length;
    const completedFolders = this.foldertransferView.rootItems.filter(
      (item) => item.status === TRANSFER_STATUS.STATUS_SUCCESS || item.status === TRANSFER_STATUS.STATUS_ERROR,
    ).length;
    return `${completed + completedFolders} of ${this.queueLength}`;
  }

  public get e2eeZipQueue(): E2eeZipItem[] {
    return this.e2eeZipService.getQueueItems().sort((a, b) => a.created - b.created);
  }

  public async cancelE2eeZip(zipId: string): Promise<void> {
    await this.e2eeZipService.cancelZip(zipId);
  }

  public removeE2eeZip(zipId: string): void {
    this.e2eeZipService.removeItem(zipId);
  }

  public isE2eeZipProcessing(): boolean {
    return this.e2eeZipService.isProcessing();
  }

  public testFileUpload(): boolean {
    return this.browserSupportService.testFileUpload();
  }
  public testDirUpload(): boolean {
    return this.browserSupportService.testDirUpload();
  }

  public showUploadBtn(): boolean {
    if (this.isPublic) {
      return this.uploadable;
    } else {
      return this.router.url.includes('/files') || this.router.url.includes('/vault');
    }
  }

  public clear(): void {
    this.transferView.download.queue = this.transferView.download.queue.filter(
      (transferItem) =>
        (transferItem.status != 1 && transferItem.status <= 1000) || ![10, 20].includes(transferItem.type),
    );

    this.transferView.upload.queue = this.transferView.upload.queue.filter(
      (transferItem) =>
        (transferItem.status != 1 && transferItem.status <= 1000) || ![10, 20].includes(transferItem.type),
    );
  }

  public downloadCompatible(file: ITransferItem): void {
    this.buildTransferItemService.getCompatUrl(file as any, this.buildTransferItemService.ACT_DOWNLOAD).then((data) => {
      window.location.href = data;
      file.status = TRANSFER_STATUS.STATUS_SUCCESS;
    });
  }

  public sort(a: TransferItem, b: TransferItem): number {
    return compare(a._initTime, b._initTime, true);
  }

  public uploadFileSelect(event: any): void {
    this.ufile = event.target.files;
  }

  public cancelUpload(transferItem: TransferItem): void {
    if (transferItem.status >= 10 && transferItem.status <= 20) {
      transferItem.status = TRANSFER_STATUS.STATUS_UPLOAD_CANCELLED;
      const processingCount = this.transferView.upload.queue.filter(
        (item) => item.status !== TRANSFER_STATUS.STATUS_UPLOAD_CANCELLED,
      ).length;
      if (processingCount === 0) {
        this.transferView.upload.isProcessing = false;
      }
      if (this.transferView.upload.queue.length > 0) {
        this.buildTransferItemService.runQueue(TransferQueueType.UPLOAD);
      }
    } else {
      transferItem.status = TRANSFER_STATUS.STATUS_UPLOAD_CANCELLED;
    }
  }
  public cancelFolderUpload(transferItem: IDirItem): void {
    if (transferItem.status === TRANSFER_STATUS.STATUS_WAITING) {
      transferItem.status = TRANSFER_STATUS.STATUS_UPLOAD_CANCELLED;
      for (let i = 0, len = this.runUploadFolderService.workFiles.length; i < len; i++) {
        const tItem = this.runUploadFolderService.workFiles[i];
        const rootPath = tItem.fullPath.split('/').slice(0, 2).join('/');
        if (rootPath === transferItem.fullPath) {
          this.runUploadFolderService.workFiles[i].status = TRANSFER_STATUS.STATUS_UPLOAD_CANCELLED;
        }
      }
    }
  }

  public cancelDownload(transferItem: TransferItem): void {
    if (transferItem.status >= 10 && transferItem.status <= 20) {
      transferItem.status = TRANSFER_STATUS.STATUS_DOWNLOAD_CANCELLED;
      const processingCount = this.transferView.download.queue.filter(
        (item) => item.status !== TRANSFER_STATUS.STATUS_DOWNLOAD_CANCELLED,
      ).length;
      if (processingCount === 0) {
        this.transferView.download.isProcessing = false;
      }
      if (this.transferView.download.queue.length > 0) {
        this.buildTransferItemService.runQueue(TransferQueueType.DOWNLOAD);
      }
    } else {
      transferItem.status = TRANSFER_STATUS.STATUS_DOWNLOAD_CANCELLED;
    }
  }

  public shouldShowRetryButton(item: ITransferItemUpload, index: number): boolean {
    const shouldShow =
      this.transferIndex === index && item.retryCount < 3 && item.status >= 7000 && item.status <= 7244;

    if (shouldShow && !this.isDisplayRetryButtonEventLogged) {
      this.blendService.track(BlendEvent.RETRY_UPLOAD_BUTTON_DISPLAYED);
      this.isDisplayRetryButtonEventLogged = true;
    }

    return shouldShow;
  }

  public async retryUpload(transferItem: ITransferItemUpload): Promise<void> {
    if (transferItem.retryCount < 3) {
      if (transferItem.status >= 7000 && transferItem.status <= 7244) {
        transferItem.chunkqueue = this.transferItemService.getChunkQueue(transferItem);
        transferItem.bytes_sent = 0;
        transferItem.progress_percent = 0;
        transferItem.progress_network = 0;
        transferItem.progress_crypt = 0;
        transferItem.status = TRANSFER_STATUS.STATUS_WAITING;
        await this.buildTransferItemService.runQueue(TransferQueueType.UPLOAD);
        this.blendService.track(BlendEvent.BUTTON_CLICKED, { button_text: 'Retry upload' });
      }
      ++transferItem.retryCount;
    }
  }

  public previewItem(transferItem: TransferItem): void {
    this.previewFile.emit(transferItem);
  }

  public handlePreviewFolder(transferItem: IDirItem | ITransferItem): void {
    this.openFolder.emit({ transferItem, id: transferItem.id });
  }

  public handleOpenFolder(transferItem: TransferItem): void {
    this.openFolder.emit({ transferItem, id: transferItem.sync_pid });
  }

  public trackbyFunction(index): number {
    return index;
  }

  public onAllFilter = (): void => {
    this.filterBy = 'all';
  };
  public onUploadFilter = (): void => {
    this.filterBy = 'upload';
  };
  public onDownloadFilter = (): void => {
    this.filterBy = 'download';
  };
  public onCloseTransfer = (): void => {
    this.transferService.closeTransfer();
    this.buildTransferItemService.resetView();
    this.runUploadFolderService.reset();
  };
  public onMinimizeToggle = (): void => {
    this.transferView.isMinimized = !this.transferView.isMinimized;
  };

  public trackByQueueItem = (index: number, item: ITransferItem): number => {
    return item.sync_id;
  };

  public trackByFolderItem = (index: number, item: IDirItem): number => {
    return item.id;
  };

  public trackByE2eeZipItem = (index: number, item: E2eeZipItem): string => {
    return item.id;
  };
}
